import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { questionnaireService } from '../../services/questionnaireService';
import { fileService } from '../../services/fileService';
import { QuestionnaireDefinitionData } from '../../types/questionnaire.types';
import { QuestionnaireWizardBody } from './QuestionnaireWizardBody';
import { calculateRequiredScores } from '../../utils/questionnaireWizardUtils';
import { determineStartingSection, buildAnswersMap } from '../../utils/questionnaireFieldUtils';

interface GenericQuestionnaireWizardProps {
  open: boolean;
  onClose: () => void;
  questionnaireId: number;
  questionnaireName: string;
  tenantId: number;
  onSave?: () => void;
  clinicState?: string | null;
  isEmbedded?: boolean;
}

/**
 * Generic questionnaire wizard for any database-defined questionnaire.
 * Handles multi-section questionnaires with various field types.
 * Delegates all wizard UI/logic to QuestionnaireWizardBody.
 */
export const GenericQuestionnaireWizard: React.FC<GenericQuestionnaireWizardProps> = ({
  open,
  onClose,
  questionnaireId,
  questionnaireName,
  tenantId,
  onSave,
  clinicState,
  isEmbedded = false,
}) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [definition, setDefinition] = useState<QuestionnaireDefinitionData | null>(null);
  const [definitionLoading, setDefinitionLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  // clinic_state from questionnaire type's clinic (overrides the prop when available)
  const [resolvedClinicState, setResolvedClinicState] = useState<string | null | undefined>(clinicState);

  // Pre-loaded state for the wizard body
  const [initialAnswers, setInitialAnswers] = useState<Record<string, any>>({});
  const [initialSectionIndex, setInitialSectionIndex] = useState(0);
  const [initialScores, setInitialScores] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Bumped after a submit to re-fetch answers and re-render the embedded wizard in place.
  const [reloadKey, setReloadKey] = useState(0);

  // ── Load questionnaire type definition ────────────────────────────────────

  useEffect(() => {
    const fetchDefinition = async () => {
      try {
        setDefinitionLoading(true);

        const questionnaire = await questionnaireService.getPatientQuestionnaire(
          questionnaireId,
          tenantId
        );

        // Use clinic_state from questionnaire type's clinic when available
        if (questionnaire.clinic_state != null) {
          setResolvedClinicState(questionnaire.clinic_state);
        }

        const questionnaireTypePath = (questionnaire as any).questionnaire_type?.path;

        if (!questionnaireTypePath) {
          setError('Questionnaire type path not found');
          return;
        }

        const questionnaireType = await questionnaireService.getPublicQuestionnaireTypeByPath(
          tenantId,
          questionnaireTypePath
        );

        if (questionnaireType?.definition) {
          setDefinition(questionnaireType.definition);
        } else {
          setError(`Questionnaire definition not found`);
        }
      } catch (err) {
        setError('Failed to load questionnaire');
      } finally {
        setDefinitionLoading(false);
      }
    };

    if (open) {
      fetchDefinition();
    }
  }, [open, questionnaireId, tenantId]);

  // ── Load existing questionnaire data (answers, token, starting section) ───

  useEffect(() => {
    const loadQuestionnaire = async () => {
      try {
        if (!definition) return;

        setLoading(true);
        const questionnaire = await questionnaireService.getPatientQuestionnaire(
          questionnaireId,
          tenantId
        );

        if (questionnaire.token) {
          setToken(questionnaire.token);
        }

        const answersMap = buildAnswersMap(questionnaire.answers, definition.sections);
        setInitialAnswers(answersMap);
        setInitialSectionIndex(determineStartingSection(definition, answersMap));

        const submitted = ['finish', 'SUBMITTED', 'FINISHED'].includes(questionnaire.status as string);
        setIsSubmitted(submitted);

        // Calculate required scores for placeholders
        const calculatedScores = await calculateRequiredScores(
          definition,
          questionnaire.token || null
        );
        if (Object.keys(calculatedScores).length > 0) {
          setInitialScores(calculatedScores);
        }
      } catch (err) {
        console.error('Error loading questionnaire:', err);
        setError('Failed to load questionnaire data');
      } finally {
        setLoading(false);
      }
    };

    if (open && questionnaireId && definition) {
      loadQuestionnaire();
    }
  }, [open, questionnaireId, tenantId, definition, reloadKey]);

  // ── API adapters ──────────────────────────────────────────────────────────

  const ensureToken = useCallback(async (): Promise<string | null> => {
    return token;
  }, [token]);

  const saveAnswers = useCallback(
    (_tok: string, answers: any[], lastSection?: { title: string; index: number }) =>
      questionnaireService.saveSectionAnswers(questionnaireId, tenantId, answers, lastSection),
    [questionnaireId, tenantId]
  );

  const uploadFileAdapter = useCallback(
    async (_tok: string, file: globalThis.File, fieldName: string) => {
      // For Generic wizard, use authenticated upload
      // We pass the questionnaire id so the file is linked correctly
      const uploaded = await fileService.uploadFile(
        file,
        undefined,
        undefined,
        undefined,
        questionnaireId,
        fieldName
      );
      return {
        id: uploaded.id,
        file_name: uploaded.file_name,
        file_size: uploaded.file_size,
      };
    },
    [questionnaireId]
  );

  const calculateScoreApi = useCallback(
    (tok: string, scoreName: string) => questionnaireService.calculateScore(tok, scoreName),
    []
  );

  const submitQuestionnaire = useCallback(
    (_tok: string) =>
      questionnaireService.updateQuestionnaireStatus(questionnaireId, tenantId, 'FINISHED'),
    [questionnaireId, tenantId]
  );

  const onExitConfirmed = useCallback(
    async (_tok: string | null, exitLocation?: string) => {
      if (_tok) {
        await questionnaireService.exitPublicQuestionnaire(_tok, exitLocation);
      }
    },
    []
  );

  const onExitNavigate = useCallback(
    (exitUrl?: string) => {
      if (isEmbedded) {
        onClose();
        return;
      }
      if (exitUrl) {
        window.location.href = exitUrl;
      } else {
        navigate('/questionnaire/exited');
      }
    },
    [isEmbedded, onClose, navigate]
  );

  const onComplete = useCallback(() => {
    toast.success('Questionnaire completed!');
    onSave?.();
    if (isEmbedded) {
      // Refresh the embedded questionnaire in place (re-fetch saved answers) instead of
      // closing it, so the meeting page shows the just-submitted state.
      setReloadKey((k) => k + 1);
    } else {
      onClose();
      navigate('/questionnaire/exited');
    }
  }, [isEmbedded, onSave, onClose, navigate]);

  // ── Render guards ─────────────────────────────────────────────────────────

  if (!open) return null;

  if (definitionLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#3A7380' }} />
      </Box>
    );
  }

  if (!definition || definition.sections.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">
          {error || `Could not load questionnaire: ${questionnaireName}`}
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#3A7380' }} />
      </Box>
    );
  }

  return (
    <QuestionnaireWizardBody
      definition={definition}
      questionnaireId={questionnaireId}
      tenantId={tenantId}
      initialToken={token}
      initialAnswers={initialAnswers}
      initialSectionIndex={isEmbedded ? 0 : initialSectionIndex}
      initialScores={initialScores}
      ensureToken={ensureToken}
      saveAnswers={saveAnswers}
      uploadFile={uploadFileAdapter}
      calculateScoreApi={calculateScoreApi}
      submitQuestionnaire={submitQuestionnaire}
      onExitConfirmed={onExitConfirmed}
      onExitNavigate={onExitNavigate}
      onComplete={onComplete}
      exitPopupVariant="questionnaire"
      isSubmitted={isEmbedded ? false : isSubmitted}
      clinicState={resolvedClinicState}
      isEmbedded={isEmbedded}
    />
  );
};
